import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { UserPlus, Mail, Trash2, Shield } from 'lucide-react';
import { usePartnerAuth, PartnerPermission } from '@/hooks/usePartnerAuth';
import { useReadOnly } from '@/contexts/ReadOnlyContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TeamMember {
  id: string;
  user_id: string | null;
  invited_email: string | null;
  invited_first_name: string | null;
  invited_last_name: string | null;
  invited_phone: string | null;
  role: 'owner' | 'staff';
  created_at: string;
  permissions: PartnerPermission[];
}

const PERMISSION_LABELS: Record<PartnerPermission, { label: string; description: string }> = {
  view_earnings: { 
    label: 'View Earnings', 
    description: 'Access to earnings dashboard, earnings history, and pricing information' 
  },
  view_team: { 
    label: 'View Team', 
    description: 'See team member list (read-only access)' 
  },
  manage_settings: { 
    label: 'Manage Settings', 
    description: 'Edit business information and service area settings' 
  },
};

const PartnerTeam = () => {
  const { partner, isOwner, hasPermission, isLoading: partnerAuthLoading } = usePartnerAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [permissionsMember, setPermissionsMember] = useState<TeamMember | null>(null);
  const [savingPermissions, setSavingPermissions] = useState<string | null>(null);
  
  const [newMember, setNewMember] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [newMemberPermissions, setNewMemberPermissions] = useState<PartnerPermission[]>([]);

  const { isReadOnly } = useReadOnly();
  const canManage = isOwner && !isReadOnly;
  const canView = hasPermission('view_team');

  const fetchTeamMembers = useCallback(async () => {
    if (!partner?.id) return;

    try {
      const { data, error } = await supabase
        .from('fulfillment_partner_users')
        .select('*')
        .eq('partner_id', partner.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch permissions for all team members
      const membersWithPermissions: TeamMember[] = [];
      for (const member of data || []) {
        let permissions: PartnerPermission[] = [];
        
        if (member.role === 'owner') {
          // Owners have all permissions
          permissions = Object.keys(PERMISSION_LABELS) as PartnerPermission[];
        } else {
          // Fetch staff permissions
          const { data: permData } = await supabase
            .from('fulfillment_partner_user_permissions')
            .select('permission')
            .eq('partner_user_id', member.id);
          
          permissions = (permData || []).map(p => p.permission as PartnerPermission);
        }

        membersWithPermissions.push({ ...member, permissions });
      }

      setTeamMembers(membersWithPermissions);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  }, [partner?.id]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const handleInviteStaff = async () => {
    if (!partner?.id || !newMember.email) return;

    setIsSubmitting(true);

    try {
      // Check if email belongs to an existing auth user
      const { data: emailCheck } = await supabase.functions.invoke('check-email-exists', {
        body: { email: newMember.email.toLowerCase() }
      });
      const existingUserId = emailCheck?.userId || null;

      // Create the database record and get the new id
      const { data: newUser, error } = await supabase
        .from('fulfillment_partner_users')
        .insert({
          partner_id: partner.id,
          role: 'staff',
          invited_email: newMember.email,
          invited_first_name: newMember.firstName,
          invited_last_name: newMember.lastName,
          invited_phone: newMember.phone,
          user_id: existingUserId
        })
        .select('id')
        .single();

      if (error) throw error;

      // Insert permissions if any were selected
      if (newMemberPermissions.length > 0 && newUser) {
        const { error: permError } = await supabase
          .from('fulfillment_partner_user_permissions')
          .insert(
            newMemberPermissions.map(perm => ({
              partner_user_id: newUser.id,
              permission: perm,
            }))
          );

        if (permError) {
          console.error('Error adding permissions:', permError);
        }
      }

      // Send invitation email
      const { error: emailError } = await supabase.functions.invoke('send-fulfillment-partner-invite', {
        body: {
          email: newMember.email,
          partnerName: partner.name,
          partnerId: partner.id,
          firstName: newMember.firstName,
          lastName: newMember.lastName,
          existingUser: !!existingUserId
        }
      });

      if (emailError) {
        console.error('Email error:', emailError);
        toast.warning('Staff added but invitation email failed to send');
      } else {
        toast.success('Staff member invited successfully');
      }

      setIsDialogOpen(false);
      setNewMember({ firstName: '', lastName: '', email: '', phone: '' });
      setNewMemberPermissions([]);
      fetchTeamMembers();
    } catch (error: unknown) {
      console.error('Error inviting staff:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to invite staff member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveStaff = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;

    try {
      const { error } = await supabase
        .from('fulfillment_partner_users')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
      toast.success('Team member removed');
      fetchTeamMembers();
    } catch (error) {
      console.error('Error removing staff:', error);
      toast.error('Failed to remove team member');
    }
  };

  const handleResendInvite = async (member: TeamMember) => {
    if (!partner || !member.invited_email) return;

    try {
      let isExistingUser = !!member.user_id;
      if (!isExistingUser) {
        const { data: emailCheck } = await supabase.functions.invoke('check-email-exists', {
          body: { email: member.invited_email.toLowerCase() }
        });
        isExistingUser = !!emailCheck?.userId;
      }

      const { error } = await supabase.functions.invoke('send-fulfillment-partner-invite', {
        body: {
          email: member.invited_email,
          partnerName: partner.name,
          partnerId: partner.id,
          firstName: member.invited_first_name,
          lastName: member.invited_last_name,
          existingUser: isExistingUser
        }
      });

      if (error) throw error;
      toast.success('Invitation resent');
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error('Failed to resend invitation');
    }
  };

  const handleTogglePermission = async (member: TeamMember, permission: PartnerPermission) => {
    if (!canManage) return;
    
    setSavingPermissions(member.id);
    const hasPermissionNow = member.permissions.includes(permission);

    try {
      if (hasPermissionNow) {
        // Remove permission
        const { error } = await supabase
          .from('fulfillment_partner_user_permissions')
          .delete()
          .eq('partner_user_id', member.id)
          .eq('permission', permission);

        if (error) throw error;
      } else {
        // Add permission
        const { error } = await supabase
          .from('fulfillment_partner_user_permissions')
          .insert({
            partner_user_id: member.id,
            permission: permission,
          });

        if (error) throw error;
      }

      // Update local state
      setTeamMembers(prev => prev.map(m => {
        if (m.id === member.id) {
          return {
            ...m,
            permissions: hasPermissionNow
              ? m.permissions.filter(p => p !== permission)
              : [...m.permissions, permission],
          };
        }
        return m;
      }));

      // Also update permissionsMember if it's the same member
      setPermissionsMember(prev => {
        if (prev && prev.id === member.id) {
          return {
            ...prev,
            permissions: hasPermissionNow
              ? prev.permissions.filter(p => p !== permission)
              : [...prev.permissions, permission],
          };
        }
        return prev;
      });

    } catch (error) {
      console.error('Error updating permission:', error);
      toast.error('Failed to update permission');
    } finally {
      setSavingPermissions(null);
    }
  };

  if (partnerAuthLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!isOwner && !canView) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team</h1>
          <p className="text-muted-foreground">
            {canManage ? 'Manage your team members' : 'View your team members'}
          </p>
        </div>
        {canManage && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Staff
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>Invite Staff Member</DialogTitle>
                <DialogDescription>
                  Send an invitation to join your team
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto py-4">
                <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={newMember.firstName}
                      onChange={(e) => setNewMember({ ...newMember, firstName: e.target.value })}
                      placeholder="John"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={newMember.lastName}
                      onChange={(e) => setNewMember({ ...newMember, lastName: e.target.value })}
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email <span className="required-asterisk">*</span></Label>
                  <Input
                    id="email"
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    placeholder="staff@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={newMember.phone}
                    onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium">Permissions</Label>
                  <p className="text-sm text-muted-foreground mb-3">Set what this staff member can access</p>
                  <div className="space-y-3">
                    {(Object.keys(PERMISSION_LABELS) as PartnerPermission[]).map((perm) => (
                      <div key={perm} className="flex items-start gap-3 p-3 rounded-lg border">
                        <Checkbox
                          id={`new-${perm}`}
                          checked={newMemberPermissions.includes(perm)}
                          onCheckedChange={(checked) => {
                            setNewMemberPermissions(prev =>
                              checked
                                ? [...prev, perm]
                                : prev.filter(p => p !== perm)
                            );
                          }}
                        />
                        <div className="flex-1">
                          <Label htmlFor={`new-${perm}`} className="font-medium cursor-pointer">
                            {PERMISSION_LABELS[perm].label}
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {PERMISSION_LABELS[perm].description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button onClick={handleInviteStaff} disabled={isSubmitting || !newMember.email}>
                  <Mail className="h-4 w-4 mr-2" />
                  {isSubmitting ? 'Sending...' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>
            {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  {canManage && <TableHead>Permissions</TableHead>}
                  {canManage && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">
                      {member.invited_first_name || ''} {member.invited_last_name || ''}
                      {!member.invited_first_name && !member.invited_last_name && '-'}
                    </TableCell>
                    <TableCell>{member.invited_email || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
                        {member.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.user_id ? 'default' : 'outline'}>
                        {member.user_id ? 'Active' : 'Pending'}
                      </Badge>
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        {member.role === 'owner' ? (
                          <span className="text-sm text-muted-foreground">All permissions</span>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setPermissionsMember(member)}
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            {member.permissions.length} permission{member.permissions.length !== 1 ? 's' : ''}
                          </Button>
                        )}
                      </TableCell>
                    )}
                    {canManage && (
                      <TableCell className="text-right">
                        {member.role === 'staff' && (
                          <div className="flex justify-end gap-2">
                            {!member.user_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResendInvite(member)}
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveStaff(member.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Permissions Dialog */}
      <Dialog 
        open={!!permissionsMember} 
        onOpenChange={(open) => !open && setPermissionsMember(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Permissions</DialogTitle>
            <DialogDescription>
              Set access levels for {permissionsMember?.invited_first_name} {permissionsMember?.invited_last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {(Object.keys(PERMISSION_LABELS) as PartnerPermission[]).map((perm) => (
              <div key={perm} className="flex items-start gap-3 p-3 rounded-lg border">
                <Checkbox
                  id={`dialog-${perm}`}
                  checked={permissionsMember?.permissions.includes(perm)}
                  onCheckedChange={() => {
                    if (permissionsMember) {
                      handleTogglePermission(permissionsMember, perm);
                    }
                  }}
                  disabled={savingPermissions === permissionsMember?.id}
                />
                <div className="flex-1">
                  <Label htmlFor={`dialog-${perm}`} className="font-medium cursor-pointer">
                    {PERMISSION_LABELS[perm].label}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {PERMISSION_LABELS[perm].description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setPermissionsMember(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerTeam;
